import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, Save, Loader2, Lock, Check, X } from 'lucide-react';

const MODULE_LABELS = {
    dashboard: 'Dashboard',
    raw_materials: 'Raw Materials',
    raw_material_inventory: 'Material Inventory',
    inventory: 'Inventory',
    production: 'Production',
    sales_orders: 'Sales Orders',
    purchase_orders: 'Purchase Orders',
    bom: 'Bill of Materials',
    finished_goods: 'Finished Goods',
    catalog: 'Catalog & Brochures',
    reports: 'Reports',
    users: 'User Management',
    warehouse_locations: 'Warehouse Locations',
    customers: 'Customers',
};

const ACTION_LABELS = {
    view: 'View',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    export: 'Export',
};

const RolePermissions = () => {
    const [roles, setRoles] = useState([]);
    const [modules, setModules] = useState([]);
    const [actions, setActions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [editPerms, setEditPerms] = useState({});
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchRoles = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/roles`);
            setRoles(res.data.roles);
            setModules(res.data.modules);
            setActions(res.data.actions);
        } catch (err) {
            toast.error('Failed to load roles');
        }
    }, []);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const selectRole = (role) => {
        setSelectedRole(role);
        setEditPerms(JSON.parse(JSON.stringify(role.permissions)));
        setDirty(false);
    };

    const togglePerm = (module, action) => {
        setEditPerms((prev) => {
            const next = { ...prev };
            const list = [...(next[module] || [])];
            const idx = list.indexOf(action);
            if (idx >= 0) list.splice(idx, 1);
            else list.push(action);
            next[module] = list;
            return next;
        });
        setDirty(true);
    };

    const toggleModuleAll = (module) => {
        setEditPerms((prev) => {
            const next = { ...prev };
            const current = next[module] || [];
            next[module] = current.length === actions.length ? [] : [...actions];
            return next;
        });
        setDirty(true);
    };

    const toggleActionAll = (action) => {
        setEditPerms((prev) => {
            const next = { ...prev };
            const allHave = modules.every((m) => (next[m] || []).includes(action));
            modules.forEach((m) => {
                const list = [...(next[m] || [])];
                if (allHave) {
                    next[m] = list.filter((a) => a !== action);
                } else if (!list.includes(action)) {
                    next[m] = [...list, action];
                }
            });
            return next;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        if (!selectedRole) return;
        setSaving(true);
        try {
            await axios.put(`${API}/roles/${selectedRole.name}`, { permissions: editPerms });
            toast.success(`Permissions for "${selectedRole.name}" saved`);
            await fetchRoles();
            setSelectedRole((prev) => ({ ...prev, permissions: editPerms }));
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleCreate = async () => {
        if (!newRoleName.trim()) return;
        setCreating(true);
        try {
            const res = await axios.post(`${API}/roles`, { name: newRoleName.trim() });
            toast.success(res.data.message);
            setNewRoleName('');
            setDialogOpen(false);
            await fetchRoles();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create role');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedRole) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/roles/${selectedRole.name}`);
            toast.success(`Role "${selectedRole.name}" deleted`);
            setSelectedRole(null);
            setEditPerms({});
            setDirty(false);
            await fetchRoles();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete role');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(145,60%,20%)] flex items-center justify-center">
                        <Shield className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1
                            className="text-2xl font-semibold text-[hsl(145,60%,20%)] tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Roles &amp; Permissions
                        </h1>
                        <p className="text-sm text-stone-500">Manage roles and their granular access levels</p>
                    </div>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                            <Plus className="mr-2 h-4 w-4" /> New Role
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Create New Role</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-2">
                            <div>
                                <Label htmlFor="roleName">Role Name</Label>
                                <Input
                                    id="roleName"
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value.replace(/[^a-zA-Z0-9_ ]/g, ''))}
                                    placeholder="e.g. warehouse_staff"
                                    className="mt-1"
                                    autoFocus
                                />
                                <p className="text-xs text-stone-400 mt-1">
                                    Alphanumeric and underscores only. New roles start with view-only access.
                                </p>
                            </div>
                            <Button
                                onClick={handleCreate}
                                disabled={creating || !newRoleName.trim()}
                                className="w-full bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                            >
                                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                                Create Role
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                {/* Role List */}
                <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                        <h2 className="text-sm font-semibold text-stone-700">Roles</h2>
                    </div>
                    <div className="divide-y divide-stone-100">
                        {roles.map((role) => (
                            <button
                                key={role.name}
                                onClick={() => selectRole(role)}
                                className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${selectedRole?.name === role.name
                                    ? 'bg-[hsl(145,60%,95%)] border-l-[3px] border-l-[hsl(145,60%,30%)]'
                                    : 'hover:bg-stone-50 border-l-[3px] border-l-transparent'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    {role.is_system && <Lock className="h-3 w-3 text-stone-400" />}
                                    <span className={`text-sm font-medium ${selectedRole?.name === role.name ? 'text-[hsl(145,60%,20%)]' : 'text-stone-700'}`}>
                                        {role.name}
                                    </span>
                                </div>
                                {role.is_system && (
                                    <span className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">system</span>
                                )}
                            </button>
                        ))}
                        {roles.length === 0 && (
                            <div className="px-4 py-6 text-sm text-stone-400 text-center">No roles found</div>
                        )}
                    </div>
                </div>

                {/* Permission Matrix */}
                {selectedRole ? (
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        {/* Matrix Header */}
                        <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="text-sm font-semibold text-stone-700 capitalize">{selectedRole.name}</h2>
                                {selectedRole.is_system && (
                                    <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded">
                                        System Role
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!selectedRole.is_system && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                    >
                                        {deleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Trash2 className="mr-1 h-3 w-3" />}
                                        Delete
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={saving || !dirty}
                                    className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                                >
                                    {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
                                    Save
                                </Button>
                            </div>
                        </div>

                        {/* Matrix Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-stone-50/50">
                                        <th className="text-left px-4 py-2.5 text-stone-600 font-medium border-b border-stone-200 w-48">Module</th>
                                        {actions.map((action) => {
                                            const allChecked = modules.every((m) => (editPerms[m] || []).includes(action));
                                            const someChecked = !allChecked && modules.some((m) => (editPerms[m] || []).includes(action));
                                            return (
                                                <th key={action} className="px-3 py-2.5 text-center border-b border-stone-200 w-20">
                                                    <button
                                                        onClick={() => toggleActionAll(action)}
                                                        className="flex flex-col items-center gap-1 w-full group"
                                                    >
                                                        <span className="text-stone-600 font-medium text-xs group-hover:text-[hsl(145,60%,20%)] transition-colors">
                                                            {ACTION_LABELS[action] || action}
                                                        </span>
                                                        <div
                                                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${allChecked
                                                                ? 'bg-[hsl(145,60%,30%)] border-[hsl(145,60%,30%)]'
                                                                : someChecked
                                                                    ? 'bg-[hsl(145,60%,80%)] border-[hsl(145,60%,50%)]'
                                                                    : 'border-stone-300 group-hover:border-stone-400'
                                                                }`}
                                                        >
                                                            {allChecked && <Check className="h-3 w-3 text-white" />}
                                                            {someChecked && !allChecked && <span className="block w-2 h-0.5 bg-[hsl(145,60%,30%)]" />}
                                                        </div>
                                                    </button>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {modules.map((module, idx) => {
                                        const perms = editPerms[module] || [];
                                        const allChecked = perms.length === actions.length;
                                        return (
                                            <tr
                                                key={module}
                                                className={`${idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/30'} hover:bg-[hsl(145,60%,97%)] transition-colors`}
                                            >
                                                <td className="px-4 py-2.5 border-b border-stone-100">
                                                    <button
                                                        onClick={() => toggleModuleAll(module)}
                                                        className="flex items-center gap-2 group"
                                                    >
                                                        <div
                                                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${allChecked
                                                                ? 'bg-[hsl(145,60%,30%)] border-[hsl(145,60%,30%)]'
                                                                : perms.length > 0
                                                                    ? 'bg-[hsl(145,60%,80%)] border-[hsl(145,60%,50%)]'
                                                                    : 'border-stone-300 group-hover:border-stone-400'
                                                                }`}
                                                        >
                                                            {allChecked && <Check className="h-3 w-3 text-white" />}
                                                            {perms.length > 0 && !allChecked && <span className="block w-2 h-0.5 bg-[hsl(145,60%,30%)]" />}
                                                        </div>
                                                        <span className="text-stone-700 font-medium group-hover:text-[hsl(145,60%,20%)] transition-colors">
                                                            {MODULE_LABELS[module] || module}
                                                        </span>
                                                    </button>
                                                </td>
                                                {actions.map((action) => {
                                                    const checked = perms.includes(action);
                                                    return (
                                                        <td key={action} className="px-3 py-2.5 text-center border-b border-stone-100">
                                                            <Checkbox
                                                                checked={checked}
                                                                onCheckedChange={() => togglePerm(module, action)}
                                                                className={checked ? 'border-[hsl(145,60%,30%)] data-[state=checked]:bg-[hsl(145,60%,30%)] data-[state=checked]:border-[hsl(145,60%,30%)]' : ''}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Dirty indicator */}
                        {dirty && (
                            <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-700 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                Unsaved changes
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white border border-stone-200 rounded-lg flex items-center justify-center py-24">
                        <div className="text-center text-stone-400">
                            <Shield className="h-10 w-10 mx-auto mb-3 text-stone-300" />
                            <p className="text-sm font-medium">Select a role to manage permissions</p>
                            <p className="text-xs mt-1">Click on a role from the sidebar to view and edit its access levels</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RolePermissions;
